/**
 * Parse URL parameters for widget configuration
 *
 * Required parameters:
 * - user_id: Required to track design credits and save designs
 * - user_image_url: Required to use as the original image for renovation
 * - client_key: Required to activate the widget and authenticate API requests
 *
 * Optional parameters:
 * - style_codes: Array of architecture style codes
 * - color_code: Single color family code
 * - inspiration_code: Single inspiration image code
 * - default_renovation: Whether to automatically generate a design on load
 * - photo_angle: Viewing angle ('front_view', 'rear_view', 'left_view', 'right_view')
 * - origin: Host origin where the widget is embedded (e.g., 'https://dzinly.com')
 * - extra[param_name]: Any parameters with prefix extra[] will be grouped into a single extra object
 */
export function parseUrlParams(searchParams: URLSearchParams) {
  const photoAngle = searchParams.get('photo_angle');
  const validPhotoAngles = ['front_view', 'rear_view', 'left_view', 'right_view'];

  // Get parameters with new naming convention
  const styleCodes = searchParams.get('style_codes')?.split(',') || [];
  const colorCode = searchParams.get('color_code');
  const userImageUrl = searchParams.get('user_image_url');
  const inspirationCode = searchParams.get('inspiration_code');

  // Get parameters with old naming convention for backward compatibility
  const styleIds = searchParams.get('style_ids')?.split(',') || [];
  const colorIds = searchParams.get('color_ids')?.split(',') || [];
  const imageUrls = searchParams.get('image_urls')?.split(',') || [];

  // Combine new and old parameters, prioritizing new ones
  const combinedStyleCodes = styleCodes.length > 0 ? styleCodes : styleIds;
  const combinedColorIds = colorCode ? [colorCode] : colorIds;

  // Build image_urls array for backward compatibility
  let combinedImageUrls: string[] = [];
  if (userImageUrl) {
    combinedImageUrls.push(userImageUrl);
  } else if (imageUrls.length > 0) {
    combinedImageUrls = imageUrls;
  }

  // Parse extra parameters (any parameter with the format extra[key]=value)
  const extraParams: Record<string, string | number> = {};

  for (const [key, value] of searchParams.entries()) {
    // Check if the parameter key matches the pattern extra[something]
    const match = key.match(/^extra\[([^\]]+)\]$/);
    if (match && match[1]) {
      const paramName = match[1];
      // For project_id, tmp_project_id, view_id, project_view_id, convert to numbers
      if (['project_id', 'tmp_project_id', 'view_id', 'project_view_id'].includes(paramName)) {
        extraParams[paramName] = parseInt(value, 10) || 0;
      } else {
        extraParams[paramName] = value;
      }
    }
  }

  // Parse upscale_resolution parameter and convert to number (default to 1 if invalid)
  const upscaleResolution = parseInt(searchParams.get('upscale_resolution') || '1', 10);
  const validUpscaleResolution = upscaleResolution >= 1 && upscaleResolution <= 5 ? upscaleResolution : 1;

  return {
    default_renovation: searchParams.get('default_renovation') === 'true',
    style_ids: combinedStyleCodes,        // For backward compatibility
    color_ids: combinedColorIds,          // For backward compatibility
    image_urls: combinedImageUrls,        // For backward compatibility
    style_codes: combinedStyleCodes,      // New parameter name
    color_code: colorCode || undefined,   // New parameter name
    user_image_url: userImageUrl || undefined, // New parameter name
    inspiration_code: inspirationCode || undefined, // New parameter name
    client_key: searchParams.get('client_key'), // Dzinly client key
    user_id: searchParams.get('user_id') || undefined,
    photo_angle: validPhotoAngles.includes(photoAngle || '')
      ? photoAngle as 'front_view' | 'rear_view' | 'left_view' | 'right_view'
      : 'front_view',
    upscale_resolution: validUpscaleResolution, // Add upscale resolution parameter
    origin: searchParams.get('origin') || undefined, // Origin of the parent website
    extra: extraParams  // Add the parsed extra parameters
  };
}

/**
 * Build URL with widget parameters
 */
export function buildWidgetUrl(params: {
  default_renovation?: boolean;
  style_codes?: string[];
  color_code?: string;
  user_image_url?: string;
  inspiration_code?: string;
  user_id?: string;
  photo_angle?: 'front_view' | 'rear_view' | 'left_view' | 'right_view';
  upscale_resolution?: number;
  origin?: string;
  // Include old parameters for backward compatibility
  style_ids?: string[];
  color_ids?: string[];
  image_urls?: string[];
  // Include extra parameters
  extra?: Record<string, string | number>;
}) {
  const url = new URL(window.location.href);

  if (params.default_renovation) {
    url.searchParams.set('default_renovation', 'true');
  } else {
    url.searchParams.delete('default_renovation');
  }

  // Use new parameter names

  if (params.style_codes && params.style_codes.length > 0) {
    url.searchParams.set('style_codes', params.style_codes.join(','));
  } else if (params.style_ids && params.style_ids.length > 0) {
    // Fallback to old parameter if new one is not provided
    url.searchParams.set('style_codes', params.style_ids.join(','));
  } else {
    url.searchParams.delete('style_codes');
  }

  if (params.color_code) {
    url.searchParams.set('color_code', params.color_code);
  } else if (params.color_ids && params.color_ids.length > 0) {
    // Fallback to first color ID if new parameter is not provided
    url.searchParams.set('color_code', params.color_ids[0]);
  } else {
    url.searchParams.delete('color_code');
  }

  if (params.user_image_url) {
    url.searchParams.set('user_image_url', params.user_image_url);
  } else if (params.image_urls && params.image_urls.length > 0) {
    // Fallback to first image URL if new parameter is not provided
    url.searchParams.set('user_image_url', params.image_urls[0]);
  } else {
    url.searchParams.delete('user_image_url');
  }

  if (params.inspiration_code) {
    url.searchParams.set('inspiration_code', params.inspiration_code);
  } else {
    url.searchParams.delete('inspiration_code');
  }

  if (params.user_id) {
    url.searchParams.set('user_id', params.user_id);
  } else {
    url.searchParams.delete('user_id');
  }

  if (params.photo_angle) {
    url.searchParams.set('photo_angle', params.photo_angle);
  } else {
    url.searchParams.delete('photo_angle');
  }

  // Handle upscale resolution parameter
  if (params.upscale_resolution && params.upscale_resolution >= 1 && params.upscale_resolution <= 5) {
    url.searchParams.set('upscale_resolution', params.upscale_resolution.toString());
  } else {
    url.searchParams.delete('upscale_resolution');
  }

  // Handle origin parameter
  if (params.origin) {
    url.searchParams.set('origin', params.origin);
  } else {
    url.searchParams.delete('origin');
  }

  // Handle extra parameters
  if (params.extra) {
    // First, remove any existing extra parameters
    for (const [key] of url.searchParams.entries()) {
      if (key.startsWith('extra[')) {
        url.searchParams.delete(key);
      }
    }

    // Then add the new ones
    for (const [key, value] of Object.entries(params.extra)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(`extra[${key}]`, value.toString());
      }
    }
  }

  return url.toString();
}